import { AsyncPipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { showErrorSwal } from '@asamco/utils';
import { Subject } from '@microsoft/signalr';
import { DxCheckBoxModule, DxDataGridModule, DxSelectBoxModule, DxTextBoxModule } from 'devextreme-angular';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxiColumnModule, DxoPagingModule, DxoSearchPanelModule, DxoSortingModule } from 'devextreme-angular/ui/nested';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { Observable, catchError, firstValueFrom, map, of, switchMap, throwError } from 'rxjs';
import { CompanyType } from '../../../enums/company-type.enum';
import { AlCompanyV2Service, CompanyDto } from '../../../services/al-company-v2.service';
import { AdminWarningBannerComponent } from '../admin-warning-banner.component';
import { RowRemovingEvent } from 'devextreme/ui/data_grid';
import { rowRemovingConfirmation } from '@asamco/application-link/shared';

type ViewModel = {
	state: 'loading'
} | {
	state: 'ready'
	dataSource: DataSource;
	parentCompanyDatasource: CompanyDto[];
} | {
	state: 'error'
}

@Component({
	selector: 'app-shell-admin-company-management-grid',
	templateUrl: './admin-company-management-grid.component.html',
	styleUrls: ['./admin-company-management-grid.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		FormsModule,
		NgIf,
		ReactiveFormsModule,
		DxTextBoxModule,
		DxCheckBoxModule,
		DxSelectBoxModule,
		DxDataGridModule,
		DxoSortingModule,
		DxoPagingModule,
		DxoSearchPanelModule,
		DxiColumnModule,
		DxTemplateModule,
		AsyncPipe,
		AdminWarningBannerComponent,
	],
})

export class AdminCompanyManagementGridComponent implements OnDestroy {
	private readonly destroy$ = new Subject<void>();

	public readonly viewModel$: Observable<ViewModel> = of({
		state: 'loading'
	}).pipe(
		switchMap(() => this.companyService.getAllPotentialParentCompanies()),
		map((potentialParentCompanies) => {
			const viewModel: ViewModel = {
				state: 'ready',
				dataSource: this.buildDataSource(),
				parentCompanyDatasource: potentialParentCompanies,
			};
			return viewModel;
		}),
		catchError(error => {
			showErrorSwal(error);
			const viewModel: ViewModel = {
				state: 'error'
			};
			return of(viewModel);
		}),
	);

	constructor(
		private companyService: AlCompanyV2Service,
	) {

	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public rowRemoving($event: RowRemovingEvent<CompanyDto>) {
		$event.cancel = rowRemovingConfirmation({
			text: `Delete company '${$event.data.name}'?`,
		});
	}

	public getCompanyEnumValue(companyTypeValue: number) {
		return CompanyType[companyTypeValue];
	}

	private buildDataSource(): DataSource {
		return new DataSource<CompanyDto, CompanyDto>({
			key: 'id',
			store: new CustomStore({
				// key: 'id',
				load: _loadOptions => {
					return firstValueFrom(this.companyService.getAll().pipe(this.handleDataStoreError()));
				},
				byKey: (key: CompanyDto) => {
					return firstValueFrom(this.companyService.get(key.id).pipe(this.handleDataStoreError()));
				},
				insert: values => {
					return firstValueFrom(this.companyService.post(values).pipe(this.handleDataStoreError()));
				},
				update: (key: CompanyDto, values: CompanyDto) => {
					// TODO: DevExtreme expects to only pass changed values. I don't want to keep track of that data
					// so that we can do a PUT. So just don't define a key, and pass the whole object as 'key'.
					const newValues =  Object.assign(key, values);
					// if (key !== values.id) {
					// 	throw new Error('ID is not the same when updating company.');
					// }
					return firstValueFrom(this.companyService.put(newValues).pipe(this.handleDataStoreError()));
				},
				remove: async (key: CompanyDto) => {
					await firstValueFrom(this.companyService.delete(key.id).pipe(this.handleDataStoreError(), map(() => undefined)));
				},
			}),
		});
	}

	private handleDataStoreError<T>(): (source: Observable<T>) => Observable<T> {
		return (source: Observable<T>): Observable<T> =>
			source.pipe(
				catchError(error => {
					showErrorSwal(error)
					// Let Dx do its thing as well.
					return throwError(() => error);
				})
			);
	}
}
